import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { useSectionSelectPortal } from "./SectionSelectPortal";

export interface SectionSideNavItem {
  id: string;
  label: string;
}

export interface SectionSideNavProps {
  items: SectionSideNavItem[];
  rootMargin?: string;
  topOffset?: number;
}

function findScrollableAncestor(el: HTMLElement): HTMLElement | Window {
  let node: HTMLElement | null = el.parentElement;
  while (node) {
    const style = window.getComputedStyle(node);
    const overflowY = style.overflowY;
    if (
      overflowY === "auto" ||
      overflowY === "scroll" ||
      overflowY === "overlay"
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return window;
}

function getScrollTop(scroller: HTMLElement | Window): number {
  return scroller instanceof HTMLElement ? scroller.scrollTop : window.scrollY;
}

export function SectionSideNav({
  items,
  rootMargin = "-80px 0px -85% 0px",
  topOffset = 80,
}: SectionSideNavProps) {
  const theme = useTheme();
  const isMobileSelect = useMediaQuery(theme.breakpoints.down("md"));

  const ids = useMemo(() => items.map((i) => i.id), [items]);
  const [activeId, setActiveId] = useState<string>(ids[0] ?? "");
  const navRef = useRef<HTMLElement | null>(null);
  const programmaticScrollRef = useRef(false);
  const programmaticTimeoutRef = useRef<number | null>(null);

  const idsKey = ids.join("|");
  useEffect(() => {
    if (idsKey.length === 0) {
      return;
    }
    const effectIds = idsKey.split("|");
    const elements = effectIds
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => Boolean(el));

    if (elements.length === 0) {
      return;
    }

    const scroller = findScrollableAncestor(elements[0]);

    const observer = new IntersectionObserver(
      (entries) => {
        if (programmaticScrollRef.current) {
          return;
        }
        const intersecting = entries.filter((entry) => entry.isIntersecting);
        if (intersecting.length === 0) {
          return;
        }
        intersecting.sort(
          (a, b) => a.boundingClientRect.top - b.boundingClientRect.top,
        );
        setActiveId(intersecting[0].target.id);
      },
      {
        root: scroller instanceof HTMLElement ? scroller : null,
        rootMargin,
        threshold: 0,
      },
    );

    elements.forEach((el) => observer.observe(el));
    return () => {
      observer.disconnect();
    };
  }, [idsKey, rootMargin]);

  useEffect(() => {
    return () => {
      if (programmaticTimeoutRef.current !== null) {
        window.clearTimeout(programmaticTimeoutRef.current);
        programmaticTimeoutRef.current = null;
      }
    };
  }, []);

  const scrollToSection = (id: string) => {
    if (id === ids[0]) {
      setActiveId(id);
      const target = document.getElementById(id);
      if (target) {
        const scroller = findScrollableAncestor(target);
        if (scroller instanceof HTMLElement) {
          scroller.scrollTo({ top: 0, behavior: "smooth" });
        } else {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      return;
    }
    const target = document.getElementById(id);
    if (!target) {
      return;
    }
    const scroller = findScrollableAncestor(target);
    const offset =
      typeof topOffset === "number" && topOffset > 0 ? topOffset : 0;
    const currentScroll = getScrollTop(scroller);
    let top: number;
    if (scroller instanceof HTMLElement) {
      const containerRect = scroller.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      top =
        targetRect.top - containerRect.top + scroller.scrollTop - offset + 1;
    } else {
      top = target.getBoundingClientRect().top + window.scrollY - offset + 1;
    }

    if (top === currentScroll) {
      setActiveId(id);
      return;
    }

    programmaticScrollRef.current = true;
    setActiveId(id);

    if (programmaticTimeoutRef.current !== null) {
      window.clearTimeout(programmaticTimeoutRef.current);
      programmaticTimeoutRef.current = null;
    }

    const releaseSuppression = () => {
      programmaticScrollRef.current = false;
      if (programmaticTimeoutRef.current !== null) {
        window.clearTimeout(programmaticTimeoutRef.current);
        programmaticTimeoutRef.current = null;
      }
    };

    const hasScrollEndSupport =
      scroller instanceof HTMLElement
        ? "onscrollend" in scroller
        : "onscrollend" in window;

    if (hasScrollEndSupport) {
      const handleScrollEnd = () => {
        scroller.removeEventListener("scrollend", handleScrollEnd);
        releaseSuppression();
      };
      scroller.addEventListener("scrollend", handleScrollEnd);
      programmaticTimeoutRef.current = window.setTimeout(() => {
        scroller.removeEventListener("scrollend", handleScrollEnd);
        releaseSuppression();
      }, 1500);
    } else {
      programmaticTimeoutRef.current = window.setTimeout(
        releaseSuppression,
        800,
      );
    }

    if (scroller instanceof HTMLElement) {
      scroller.scrollTo({ top, behavior: "smooth" });
    } else {
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  const handleClick = (id: string) => () => {
    scrollToSection(id);
  };

  const navItems = (onClick: (id: string) => () => void) =>
    items.map((item) => {
      const isActive = activeId === item.id;
      return (
        <Box
          key={item.id}
          component="button"
          type="button"
          onClick={onClick(item.id)}
          data-testid={`page-section-${item.id}-button`}
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            textAlign: "left",
            border: "none",
            cursor: "pointer",
            background: "transparent",
            px: 2,
            py: 0.75,
            ml: "-1px",
            borderLeft: 1,
            borderColor: "transparent",
            color: "text.primary",
            transition: "background-color 0.12s ease, color 0.12s ease",
            "&:hover": {
              backgroundColor: "action.hover",
            },
            ...(isActive && {
              color: "primary.main",
              backgroundColor: "primary._states.hover",
              borderColor: "primary.main",
              "&:hover": {
                backgroundColor: "primary._states.hover",
              },
            }),
          }}
        >
          <Typography variant="body1">{item.label}</Typography>
        </Box>
      );
    });

  const activeLabel = items.find((i) => i.id === activeId)?.label ?? "";
  const portalRef = useSectionSelectPortal();

  if (isMobileSelect) {
    const selectElement = (
      <Box
        ref={(node: HTMLElement | null) => {
          navRef.current = node;
        }}
        sx={{
          position: "sticky",
          top: 0,
          zIndex: theme.zIndex.appBar - 1,
          backgroundColor: "background.paper",
          mx: { xs: -2, sm: -3 },
          mt: 0,
          mb: 2,
          px: 2.5,
          py: 1,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <TextField
          select
          fullWidth
          variant="standard"
          data-testid="page-sections-select"
          sx={{
            "& .MuiInput-underline:before": { borderBottom: "none" },
            "& .MuiInput-underline:after": { borderBottom: "none" },
            "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
              borderBottom: "none",
            },
          }}
          value={activeId}
          onChange={(e) => {
            const sectionId = e.target.value;
            setActiveId(sectionId);
            setTimeout(() => scrollToSection(sectionId), 0);
          }}
          size="small"
          slotProps={{
            select: {
              renderValue: () => `Section: ${activeLabel}`,
            },
          }}
        >
          {items.map((item) => (
            <MenuItem key={item.id} value={item.id}>
              {item.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>
    );

    if (portalRef?.current) {
      return createPortal(selectElement, portalRef.current);
    }
    return selectElement;
  }

  return (
    <Stack
      component="nav"
      aria-label="Page sections"
      ref={(node: HTMLElement | null) => {
        navRef.current = node;
      }}
      sx={{
        position: "sticky",
        top: (t) => t.spacing(9.5),
        borderLeft: 1,
        borderColor: "divider",
        py: 1,
      }}
    >
      {navItems(handleClick)}
    </Stack>
  );
}
